import { useEffect, useState } from 'react';
import { HelpCircle, X, Sparkles, Target, Compass, Zap } from 'lucide-react';

/**
 * ContextualHelp — a small (?) icon that opens a slide-out panel with the
 * 4 questions every page should answer:
 *   1. What is this page?  (whatIs)
 *   2. Why does it matter? (whyMatters)
 *   3. How should I use it? (howToUse)
 *   4. Tips for the most value. (tips  — array of strings)
 *
 * Usage:
 *   <ContextualHelp pageTitle="The Lab" content={{ whatIs: '...', whyMatters: '...', howToUse: '...', tips: [...] }}/>
 */
export default function ContextualHelp({ pageTitle, content, testid = 'contextual-help', tone = 'auto' }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!content) return null;

  const triggerClass = tone === 'light'
    ? 'inline-flex items-center justify-center w-7 h-7 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-smooth'
    : 'inline-flex items-center justify-center w-7 h-7 rounded-full text-navy/45 hover:text-navy hover:bg-beige transition-smooth';

  return (
    <>
      <button
        data-testid={`${testid}-open`}
        onClick={() => setOpen(true)}
        aria-label={`How to use ${pageTitle}`}
        className={triggerClass}
      >
        <HelpCircle size={16} strokeWidth={1.75}/>
      </button>

      {open && (
        <div
          className="fixed inset-0 flex justify-end"
          style={{ zIndex: 90, backgroundColor: 'rgba(11,25,44,0.55)' }}
          onClick={() => setOpen(false)}
          data-testid={`${testid}-overlay`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg h-full flex flex-col"
            style={{ backgroundColor: '#FFFFFF', boxShadow: '-24px 0 60px -12px rgba(11,25,44,0.25)' }}
            data-testid={`${testid}-panel`}
          >
            {/* Header */}
            <div className="flex items-start justify-between px-6 lg:px-8 pt-6 lg:pt-8 pb-4 border-b" style={{ borderColor: 'rgba(11,25,44,0.08)' }}>
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">How to use</p>
                <h2 className="font-heading text-2xl lg:text-3xl mt-1 text-navy">{pageTitle}</h2>
                <p className="text-xs text-navy/45 mt-1">Under 1 minute read</p>
              </div>
              <button
                data-testid={`${testid}-close`}
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center text-navy/60 hover:text-navy hover:bg-beige transition-smooth"
              >
                <X size={16}/>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 lg:px-8 py-6 space-y-6" style={{ backgroundColor: '#F9F8F6' }}>
              {content.whatIs && (
                <Section icon={Sparkles} label="What is this page?" body={content.whatIs}/>
              )}
              {content.whyMatters && (
                <Section icon={Target} label="Why does it matter?" body={content.whyMatters}/>
              )}
              {content.howToUse && (
                <Section icon={Compass} label="How should I use it?" body={content.howToUse}/>
              )}
              {content.tips?.length > 0 && (
                <div className="bg-white rounded-2xl p-5 lg:p-6" style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
                      <Zap size={14} className="text-gold" strokeWidth={1.75}/>
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-navy/55 font-semibold">Tips for the most value</p>
                  </div>
                  <ul className="space-y-2">
                    {content.tips.map((t, i) => (
                      <li key={i} className="text-sm text-navy/75 leading-relaxed flex gap-2">
                        <span className="text-gold mt-0.5 shrink-0">•</span><span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ icon: Icon, label, body }) {
  return (
    <div className="bg-white rounded-2xl p-5 lg:p-6" style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}>
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(11,25,44,0.05)' }}>
          <Icon size={14} className="text-navy" strokeWidth={1.75}/>
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-navy/55 font-semibold">{label}</p>
      </div>
      <p className="text-sm lg:text-base text-navy/75 leading-relaxed whitespace-pre-line">{body}</p>
    </div>
  );
}
